import React, { useState } from 'react';
import { InjurySendAdminEmail, InjurySendUserEmail } from './emailService';
import { Car, Bike, Users, Truck, Building2, ChevronLeft } from 'lucide-react';
import logo1 from "./f logo.svg"
// Function to get the initial landing URL
let initialLandingUrl = null;
 
const getSourceUrl = () => {
  if (typeof window === "undefined") return "Unknown";
 
  // If we haven't stored the initial URL yet, store it
  if (!initialLandingUrl) {
    initialLandingUrl = window.location.href;
  }
 
  return initialLandingUrl;
};
 
// Function to get IP address
const getIPAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Failed to get IP address:", error);
    return "IP address not available";
  }
};


// Function to get Form Details
const getFormDetails = () => {
  const path = window.location.pathname; 

  if (path === "/" || path === "") {
    return { formname: "Home Form" };
  }

  const pageKey = path.replace("/", ""); 

  const readable = pageKey
    .replace(/Main$/i, "") 
    .replace(/([a-z])([A-Z])/g, "$1 $2") 
    .trim();

  return { formname: `${readable} Form` };
};
const InjuryCompensationForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    accidentType: '',
    zipCode: '',
    treatment: '',
    policeReport: '',
    atFault: '',
    represented: '',
    accidentTimeframe: '',
    primaryInjury: '',
    injuryDescription: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // New: validation error state for name and contact fields
  const [nameErrors, setNameErrors] = useState({ firstName: '', lastName: '' });
  const [contactErrors, setContactErrors] = useState({ phone: '', email: '' });

  const totalSteps = 13;
  const progress = (step / totalSteps) * 100;

  const accidentTypes = [
    { id: 'car', label: 'Car', icon: Car, color: 'bg-blue-600 hover:bg-blue-700' },
    { id: 'pedestrian', label: 'Pedestrian or Bicycle', icon: Bike, color: 'bg-slate-700 hover:bg-slate-800' },
    { id: 'motorcycle', label: 'Motorcycle', icon: Bike, color: 'bg-slate-700 hover:bg-slate-800' },
    { id: 'rideshare', label: 'Rideshare', icon: Users, color: 'bg-slate-700 hover:bg-slate-800' },
    { id: 'government', label: 'Government Vehicle', icon: Building2, color: 'bg-slate-700 hover:bg-slate-800' },
    { id: 'trucks', label: 'Trucks', icon: Truck, color: 'bg-slate-700 hover:bg-slate-800' }
  ];

  const treatmentOptions = [
    { id: 'hospital', label: 'Treated at a hospital' },
    { id: 'doctor', label: 'Treated at a doctors\' office' },
    { id: 'not-treated', label: 'Was not treated' }
  ];

  const handleAccidentTypeSelect = (type) => {
    setFormData({ ...formData, accidentType: type });
    setStep(2);
  };

  const handleZipSubmit = (e) => {
    e.preventDefault();
    if (formData.zipCode) {
      setStep(3);
    }
  };

  const handleTreatmentSelect = (treatment) => {
    setFormData({ ...formData, treatment });
    setStep(4);
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handlePoliceReport = (value) => {
    const nextStep = value === 'yes' ? 6 : 7; // skip at-fault if no report
    setFormData({ ...formData, policeReport: value, atFault: value === 'yes' ? formData.atFault : '' });
    setStep(nextStep);
  };

  const handleAtFault = (value) => {
    setFormData({ ...formData, atFault: value });
    setStep(7);
  };

  const handleRepresented = (value) => {
    setFormData({ ...formData, represented: value });
    setStep(8);
  };

  const handleAccidentTimeframe = (value) => {
    setFormData({ ...formData, accidentTimeframe: value });
    setStep(9);
  };

  const handlePrimaryInjury = (e) => {
    setFormData({ ...formData, primaryInjury: e.target.value });
  };

  const handleInjuryDescription = (e) => {
    const value = e.target.value.slice(0, 3000);
    setFormData({ ...formData, injuryDescription: value });
  };

  // Improved validators
  const validateName = (name) => {
    if (!name) return false;
    const trimmed = name.trim();
    if (trimmed.length < 2) return false;
    // allow letters (including accented), spaces, hyphens and apostrophes
    return /^[A-Za-z\u00C0-\u017F' -]+$/.test(trimmed);
  };

  const validatePhone = (value) => {
    if (!value) return false;
    // strip all non-digit characters and require exactly 10 digits
    const digits = value.replace(/\D/g, '');
    return digits.length === 10;
  };

  const validateEmail = (value) => {
    if (!value) return false;
    // basic RFC-like email check with min TLD length
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  };

  const handleNameNext = (e) => {
    e.preventDefault();
    // validate first and last name
    const firstValid = validateName(formData.firstName);
    const lastValid = validateName(formData.lastName);
    const newErrors = { firstName: '', lastName: '' };

    if (!firstValid) newErrors.firstName = 'Please enter a valid first name (letters, hyphens or spaces).';
    if (!lastValid) newErrors.lastName = 'Please enter a valid last name (letters, hyphens or spaces).';

    setNameErrors(newErrors);

    if (firstValid && lastValid) {
      setStep(12);
    }
  };

  const handleContactNext = (e) => {
    e.preventDefault();
    const phoneValid = validatePhone(formData.phone);
    const emailValid = validateEmail(formData.email);
    const newErrors = { phone: '', email: '' };

    if (!phoneValid) newErrors.phone = 'Please enter a valid phone number.';
    if (!emailValid) newErrors.email = 'Please enter a valid email address.';

    setContactErrors(newErrors);

    if (phoneValid && emailValid) {
      setStep(13);
    }
  };

  const validateFormData = () => {
    const requiredFields = ['accidentType', 'zipCode', 'treatment', 'policeReport', 'represented', 'accidentTimeframe', 'primaryInjury', 'firstName', 'lastName', 'phone', 'email'];
    const missingFields = requiredFields.filter(field => !formData[field] || formData[field].trim() === '');
    
    if (missingFields.length > 0) {
      console.warn('⚠️ Missing required fields:', missingFields);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError('');
    setSubmitting(true);

    // Validate form data before submission
    if (!validateFormData()) {
      setSubmitError('Please complete all required fields before submitting.');
      setSubmitting(false);
      return;
    }
    
    try {
      console.log('📝 Starting form submission...');
      console.log('📋 Complete form data being sent:', formData);
      console.log('📊 Form data summary:', {
        'Accident Type': formData.accidentType,
        'Zip Code': formData.zipCode,
        'Treatment': formData.treatment,
        'Police Report': formData.policeReport,
        'At Fault': formData.atFault,
        'Represented': formData.represented,
        'Timeframe': formData.accidentTimeframe,
        'Primary Injury': formData.primaryInjury,
        'Injury Description': formData.injuryDescription ? `${formData.injuryDescription.substring(0, 100)}...` : 'None',
        'Name': `${formData.firstName} ${formData.lastName}`,
        'Phone': formData.phone,
        'Email': formData.email
      });
      
      // Send admin email
      console.log('📧 Sending admin email...');
      const adminResult = await InjurySendAdminEmail(formData);
      console.log('📧 Admin email result:', adminResult);
      await fetch(
  'https://crm-internal-backend-ayb9fqawg8b6bjen.canadacentral-01.azurewebsites.net/api/submitformdata',
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      countryName: 'Usa',
      brandName: 'Personal Injury',
      websiteName: 'Claim4Accidents',
      formname: getFormDetails().formname,
      data: {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phoneNumber,
        accidentType: formData.accidentType,
        injuryLevel: formData.injuryLevel,
        whoWasInAccident: formData.whoWasInAccident,
        whoWasAtFault: formData.whoWasAtFault,
        wereYouInsured: formData.wereYouInsured,
        timezone: formData.timezone,
        scheduledDate: formData.scheduledDate,
        scheduledTime: formData.scheduledTime,
        contactPreference: formData.contactPreference,
        ipAddress: await getIPAddress(),
        submissionDate: new Date().toISOString(),
        pageSource: getSourceUrl(),
      },
    }),
  }
);
      // Send user confirmation email
      console.log('📧 Sending user email...');
      const userResult = await InjurySendUserEmail(formData);
      console.log('📧 User email result:', userResult);
      
      // Check if both emails were successful
      if (adminResult.success && userResult.success) {
        alert('Thank you! We\'ve received your details.');
        setStep(1);
        setFormData({
          accidentType: '',
          zipCode: '',
          treatment: '',
          policeReport: '',
          atFault: '',
          represented: '',
          accidentTimeframe: '',
          primaryInjury: '',
          injuryDescription: '',
          firstName: '',
          lastName: '',
          phone: '',
          email: ''
        });
      } else {
        throw new Error('One or more emails failed to send');
      }
    } catch (err) {
      console.error('❌ Form submission error:', err);
      setSubmitError('Failed to submit. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="injury-compensation-form" className="min-h-screen relative overflow-hidden" style={{ backgroundImage: 'url(https://dwy9ix7d387oz.cloudfront.net/attachments/2025/8/7c114d4e-9eaa-4ab6-846c-460bd9fd588e.webp)', backgroundSize: '100% 100%', backgroundPosition: 'center' }}>
      {/* SEO Meta Tags */}
      <div style={{ display: 'none' }}>
        <h1>Top Auto Accident Lawyers Near You - Free Case Evaluation</h1>
        <meta name="description" content="Get connected with the best injury compensation lawyers. No win, no fee. Free case evaluation with no obligation." />
      </div>

      {/* Background Image Effect */}
      <div className="absolute inset-0 bg-black/40"></div>
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6" role="banner">
          <nav className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2" aria-label="Main navigation">
            <div className="flex items-center gap-2">
              {/* <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white rounded-full relative">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full"></div>
                </div>
              </div> */}
              <img src={logo1} alt="Injury Compensation Logo" className="h-8 w-auto" />
            </div>
            <a href="tel:+18882021350" className="flex items-center gap-2 text-white text-sm sm:text-base font-medium text-center sm:text-right no-underline" style={{ textDecoration: 'none' }}>
              <span className="w-7 h-7 lg:w-9 lg:h-9 mr-2 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" fill="none" viewBox="0 0 100 100" className="w-7 h-7 lg:w-9 lg:h-9 mr-2">
                  <path fill="#E53E3E" fillRule="evenodd" d="M55.593 93.297a7.215 7.215 0 01-7.104 5.89c-3.988 0-7.221-3.22-7.221-7.19 0-3.972 3.233-7.19 7.221-7.19a7.225 7.225 0 016.423 3.898c19.102-2.683 29.983-11.136 32.641-25.357-1.133.603-2.692-.386-4.677-2.968 0 0-7.57 17.754-19.538 20.67 17.51-14.738 5.792-37.794 3.174-42.574-2.618-4.78-3.624-6.694-4.849.999-2.84 12.297-30.994 5.092-35.061 16.062-2.712 7.314.604 16.086 9.948 26.316-10.126-3.079-17.001-9.73-20.624-19.952C11.485 68.329 3.16 63.274 3 59.26V40.364c.004-3.38 1.985-5.488 5.942-6.322 8.587-42.238 69.546-45.8 80.114-1.489 4.971 0 7.62 1.975 7.944 5.923l-.426 18.425c.048 2.922-1.37 5.071-4.253 6.447-3.84 16.7-16.082 26.683-36.728 29.95zm-39.41-56.535C23.38 20.998 34.653 13.43 50 14.057c21.029.774 30.198 15.997 32.166 22.705l.91-.899C77.693 16.976 66.411 7.533 49.23 7.533c-17.181 0-28.464 9.443-33.847 28.33l.8.9z" clipRule="evenodd"></path>
                </svg>
              </span>
              <span className="font-bold text-xl">+1 (888) 202-1350</span>
            </a>
          </nav>
        </header>

        {/* Main Content */}
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 sm:py-12" role="main">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Section */}
            <section className="text-white space-y-6 sm:space-y-8" aria-labelledby="main-heading">
              <div className="flex items-center gap-2 text-red-400">
                <div className="w-6 h-6 border-2 border-red-400 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-base sm:text-lg font-semibold">No Win, No Fee</span>
              </div>

              <h2 id="main-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Top Auto Accident Lawyers Near You
              </h2>

              <p className="text-lg sm:text-xl text-gray-300">
                Complete the Short Form to Get Your Free Case Review.
              </p>

              <ul className="space-y-4" role="list">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 mt-0.5 flex-shrink-0">
                    <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-base sm:text-lg">Free case evaluation - No obligation</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 mt-0.5 flex-shrink-0">
                    <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-base sm:text-lg">No fees unless your lawyer wins your case</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 mt-0.5 flex-shrink-0">
                    <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="text-base sm:text-lg">Maximize your settlement with the right negotiator</span>
                </li>
              </ul>

              <li className="flex items-start mt-6">
                <a href="tel:+18882021350" className="bg-red-600 hover:bg-red-700 text-white font-bold text-xl px-8 py-4 rounded-2xl shadow-lg flex items-center gap-4 no-underline" style={{ textDecoration: 'none', boxShadow: '0 0 0 3px #7F9CF5' }}>
                  {/* Phone call SVG icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                    <path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.13.81.36 1.6.68 2.34a2 2 0 0 1-.45 2.11l-.27.27a16 16 0 0 0 6.29 6.29l.27-.27a2 2 0 0 1 2.11-.45c.74.32 1.53.55 2.34.68a2 2 0 0 1 1.72 2z" />
                  </svg>
                  <span>(888) 202-1350</span>
                </a>
              </li>
            </section>

            {/* Right Section - Form */}
            <section className="bg-white rounded-lg shadow-2xl p-6 sm:p-8 lg:p-10" aria-label="Case evaluation form">
              {/* Step 1: Accident Type */}
              {step === 1 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">
                    What best describes the type of accident you were in?
                  </h3>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {accidentTypes.map((type) => {
                      const isSelected = formData.accidentType === type.id;
                      const baseClasses = 'text-white p-2 sm:p-3 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center gap-2 text-left text-sm sm:text-base';
                      const colorClass = isSelected ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-700 hover:bg-slate-800';

                      return (
                        <button
                          key={type.id}
                          onClick={() => handleAccidentTypeSelect(type.id)}
                          className={`${colorClass} ${baseClasses}`}
                          aria-label={`Select ${type.label}`}
                          aria-pressed={isSelected}
                        >
                          <type.icon className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" aria-hidden="true" />
                          <span className="font-semibold">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  <p className="text-xs sm:text-sm text-gray-400 text-center mt-4">
                    Karnataka Best Lawyers
                  </p>
                </div>
              )}

              {/* Step 2: Zip Code */}
              {step === 2 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    What's your zip code?
                  </h3>
                  
                  <form onSubmit={handleZipSubmit} className="space-y-4">
                    <input
                      type="text"
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      placeholder="10001"
                      className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                      maxLength="6"
                      pattern="[0-9]*"
                      required
                      aria-label="Enter zip code"
                    />
                    
                    <button
                      type="submit"
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 sm:py-4 rounded-lg transition-colors duration-200 text-lg"
                      aria-label="Continue to next step"
                    >
                      Next
                    </button>
                  </form>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-gray-400 h-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        onClick={handlePrevious}
                        className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm"
                        aria-label="Go to previous step"
                      >
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded" aria-live="polite">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Treatment */}
              {step === 3 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    Did the injured person receive treatment?
                  </h3>
                  
                  <div className="space-y-2">
                    {treatmentOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => handleTreatmentSelect(option.id)}
                        className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 sm:p-3 rounded-lg transition-all duration-200 transform hover:scale-102 text-left font-semibold text-sm sm:text-base"
                        aria-label={`Select ${option.label}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-gray-400 h-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        onClick={handlePrevious}
                        className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm"
                        aria-label="Go to previous step"
                      >
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded" aria-live="polite">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Police Report Filed */}
              {step === 4 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    Was a police report filed?
                  </h3>
                  <div className="space-y-2">
                    <button onClick={() => handlePoliceReport('yes')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">Yes</button>
                    <button onClick={() => handlePoliceReport('no')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">No</button>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gray-400 h-full transition-all duration-300" style={{ width: `${progress}%` }} role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button onClick={handlePrevious} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded" aria-live="polite">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: At Fault (only if police report yes) */}
              {step === 6 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    Did the police report say you are at fault?
                  </h3>
                  <div className="space-y-2">
                    <button onClick={() => handleAtFault('no')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">No</button>
                    <button onClick={() => handleAtFault('yes')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">Yes</button>
                    <button onClick={() => handleAtFault('both')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">Both at fault</button>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gray-400 h-full transition-all duration-300" style={{ width: `${progress}%` }} role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button onClick={() => setStep(4)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded" aria-live="polite">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 7: Represented by Lawyer */}
              {step === 7 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    Are you currently represented by a lawyer for this case?
                  </h3>
                  <div className="space-y-2">
                    <button onClick={() => handleRepresented('no')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">No</button>
                    <button onClick={() => handleRepresented('yes')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">Yes</button>
                    <button onClick={() => handleRepresented('was')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">I was, but not anymore</button>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gray-400 h-full transition-all duration-300" style={{ width: `${progress}%` }} role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button onClick={() => setStep(formData.policeReport === 'yes' ? 6 : 4)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded" aria-live="polite">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 8: Accident Timeframe */}
              {step === 8 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">When did the accident happen?</h3>
                  <div className="space-y-2">
                    <button onClick={() => handleAccidentTimeframe('30-days')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">Within the last 30 days</button>
                    <button onClick={() => handleAccidentTimeframe('1-year')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">Within the last year</button>
                    <button onClick={() => handleAccidentTimeframe('2-years')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">Within the last 2 years</button>
                    <button onClick={() => handleAccidentTimeframe('more-2-years')} className="w-full bg-slate-700 hover:bg-slate-800 text-white p-2 rounded-lg font-semibold text-sm">More than 2 years</button>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gray-400 h-full transition-all duration-300" style={{ width: `${progress}%` }} role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button onClick={() => setStep(7)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded" aria-live="polite">
                        {Math.round(progress)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 9: Primary Injury */}
              {step === 9 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">What is the primary injury?</h3>
                  <div>
                    <select value={formData.primaryInjury} onChange={handlePrimaryInjury} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none">
                      <option value="" disabled>Choose an injury</option>
                      <option value="cuts-bruises">Cuts and Bruises</option>
                      <option value="whiplash">Whiplash</option>
                      <option value="broken-bone">Broken bone</option>
                      <option value="concussion">Concussion / Head injury</option>
                      <option value="back-neck">Back/Neck pain</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gray-400 h-full transition-all duration-300" style={{ width: `${progress}%` }} role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button onClick={() => setStep(8)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <button disabled={!formData.primaryInjury} onClick={() => setStep(10)} className={`text-white font-bold py-2 px-4 rounded ${formData.primaryInjury ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400 cursor-not-allowed'}`}>Next</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 10: Describe Injury */}
              {step === 10 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">Describe how you were injured</h3>
                  <div>
                    <textarea value={formData.injuryDescription} onChange={handleInjuryDescription} rows="6" className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none" placeholder="Provide any details you think are important (optional)" />
                    <div className="text-sm text-gray-500 mt-1">You have {3000 - (formData.injuryDescription?.length || 0)} characters left</div>
                  </div>
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-gray-400 h-full transition-all duration-300" style={{ width: `${progress}%` }} role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <button onClick={() => setStep(9)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <button onClick={() => setStep(11)} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">Next</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 11: Name */}
              {step === 11 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">What is your name?</h3>
                  <p className="text-gray-600 text-sm">Personal Information Is Safe & Secure.</p>
                  <form onSubmit={handleNameNext} className="space-y-4">
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => { setFormData({ ...formData, firstName: e.target.value }); setNameErrors({ ...nameErrors, firstName: '' }); }}
                      placeholder="First Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                      required
                      aria-invalid={!!nameErrors.firstName}
                      aria-describedby={nameErrors.firstName ? 'firstName-error' : undefined}
                    />
                    {nameErrors.firstName && (
                      <div id="firstName-error" className="text-red-600 text-sm mt-1">{nameErrors.firstName}</div>
                    )}

                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => { setFormData({ ...formData, lastName: e.target.value }); setNameErrors({ ...nameErrors, lastName: '' }); }}
                      placeholder="Last Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                      required
                      aria-invalid={!!nameErrors.lastName}
                      aria-describedby={nameErrors.lastName ? 'lastName-error' : undefined}
                    />
                    {nameErrors.lastName && (
                      <div id="lastName-error" className="text-red-600 text-sm mt-1">{nameErrors.lastName}</div>
                    )}

                    <div className="flex justify-between items-center">
                      <button type="button" onClick={() => setStep(10)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <button type="submit" className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">Next</button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 12: Contact Details */}
              {step === 12 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">Contact Details</h3>
                  <p className="text-gray-600 text-sm">Your Privacy Is Our Priority - See Terms Below</p>
                  <form onSubmit={handleContactNext} className="space-y-4">
                    <div>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => {
                          // keep only digits and limit to 10 characters
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setFormData({ ...formData, phone: digits });
                          setContactErrors({ ...contactErrors, phone: '' });
                        }}
                        placeholder="Phone Number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                        required
                        aria-invalid={!!contactErrors.phone}
                        aria-describedby={contactErrors.phone ? 'phone-error' : undefined}
                        maxLength={10}
                      />
                      {contactErrors.phone && (
                        <div id="phone-error" className="text-red-600 text-sm mt-1">{contactErrors.phone}</div>
                      )}
                    </div>
                    <div>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setContactErrors({ ...contactErrors, email: '' }); }}
                        placeholder="Email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                        required
                        aria-invalid={!!contactErrors.email}
                        aria-describedby={contactErrors.email ? 'email-error' : undefined}
                      />
                      {contactErrors.email && (
                        <div id="email-error" className="text-red-600 text-sm mt-1">{contactErrors.email}</div>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <button type="button" onClick={() => setStep(11)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <button type="submit" disabled={!(validatePhone(formData.phone) && validateEmail(formData.email))} className={`text-white font-bold py-2 px-4 rounded ${validatePhone(formData.phone) && validateEmail(formData.email) ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400 cursor-not-allowed'}`}>Next</button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 13: Confirmation */}
              {step === 13 && (
                <div className="space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">What's My Case Worth?</h3>
                  <p className="text-gray-600">Review your details and submit your request for a free case evaluation.</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {submitError && (
                      <div className="text-red-600 text-sm" role="alert">{submitError}</div>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className={`w-full text-white font-bold py-3 rounded-lg ${submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                    >
                      {submitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                          </svg>
                          Submitting...
                        </span>
                      ) : (
                        'Submit'
                      )}
                    </button>
                    <div className="flex justify-between items-center">
                      <button type="button" onClick={() => setStep(12)} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-sm" aria-label="Go to previous step">
                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                        Previous
                      </button>
                      <span className="text-xs bg-gray-700 text-white px-2 py-1 rounded" aria-live="polite">{Math.round(progress)}%</span>
                    </div>
                  </form>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default InjuryCompensationForm;